import { $generateNodesFromDOM } from '@lexical/html';
import { $isLinkNode, $toggleLink } from '@lexical/link';
import { $isHeadingNode, $isQuoteNode } from '@lexical/rich-text';
import {
  $createParagraphNode,
  $findMatchingParent,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $isTextNode,
  $setSelection,
  type BaseSelection,
  type ElementNode,
  type LexicalEditor,
} from 'lexical';

export type BlockType = 'paragraph' | 'h1' | 'h2' | 'h3' | 'quote';

export const DEFAULT_FONT_FAMILY = 'Inter, sans-serif';
export const DEFAULT_FONT_SIZE = '16px';

export function normalizeEditorHtml(html?: string): string {
  if (!html) {
    return '';
  }

  const trimmedHtml = html.trim();

  if (trimmedHtml === '') {
    return '';
  }

  if (typeof globalThis.DOMParser === 'undefined') {
    return trimmedHtml;
  }

  // Legacy values can be plain text; keep their line breaks instead of collapsing them.
  const source = /<[a-z][^>]*>/i.test(trimmedHtml)
    ? trimmedHtml
    : trimmedHtml.replace(/\r?\n/g, '<br>');

  const parser = new globalThis.DOMParser();
  const document = parser.parseFromString(source, 'text/html');
  const { body } = document;
  const normalizedHtml = body.innerHTML.trim();

  if (
    body.textContent?.trim() === '' &&
    !body.querySelector(
      'img,video,iframe,table,ul,ol,blockquote,h1,h2,h3,h4,h5,h6'
    )
  ) {
    return '';
  }

  return normalizedHtml;
}

export function $setHtmlContent(editor: LexicalEditor, html?: string) {
  const root = $getRoot();
  root.clear();

  if (!html || typeof globalThis.DOMParser === 'undefined') {
    root.append($createParagraphNode());
    return;
  }

  const parser = new globalThis.DOMParser();
  const document = parser.parseFromString(html, 'text/html');
  const nodes = $generateNodesFromDOM(editor, document);

  if (nodes.length === 0) {
    root.append($createParagraphNode());
    return;
  }

  root.select();

  const selection = $getSelection();

  if ($isRangeSelection(selection)) {
    selection.insertNodes(nodes);
  }

  if (root.getChildrenSize() === 0) {
    root.append($createParagraphNode());
  }

  $setSelection(null);
}

export function loadHtmlIntoEditor(editor: LexicalEditor, html?: string) {
  editor.update(() => {
    $setHtmlContent(editor, normalizeEditorHtml(html));
  });
}

export function getSelectedBlockType(
  selection: BaseSelection | null
): BlockType {
  if (!$isRangeSelection(selection)) {
    return 'paragraph';
  }

  const topLevelElement = getSelectedTopLevelElement(selection);

  if (!topLevelElement) {
    return 'paragraph';
  }

  if ($isHeadingNode(topLevelElement)) {
    const tag = topLevelElement.getTag();

    if (tag === 'h1' || tag === 'h2' || tag === 'h3') {
      return tag;
    }
  }

  if ($isQuoteNode(topLevelElement)) {
    return 'quote';
  }

  return 'paragraph';
}

export function getSelectedTopLevelElement(
  selection: BaseSelection | null
): ElementNode | null {
  if (!$isRangeSelection(selection)) {
    return null;
  }

  const anchorNode = selection.anchor.getNode();
  const element =
    $isElementNode(anchorNode) && anchorNode.getKey() === 'root'
      ? anchorNode.getChildAtIndex(selection.anchor.offset)
      : anchorNode.getTopLevelElementOrThrow();

  return $isElementNode(element) && !$isRootOrShadowRoot(element)
    ? element
    : null;
}

export function getSelectedLinkUrl(selection: BaseSelection | null) {
  if (!$isRangeSelection(selection)) {
    return '';
  }

  const anchorNode = selection.anchor.getNode();
  const linkNode = $findMatchingParent(anchorNode, $isLinkNode);

  return linkNode?.getURL() ?? '';
}

/** Strips bold/italic/underline/strike, inline styles (color, size, font) and links from the selection. */
export function $clearSelectionFormatting() {
  const selection = $getSelection();

  if (!$isRangeSelection(selection) || selection.isCollapsed()) {
    return;
  }

  selection.extract().forEach((node) => {
    if ($isTextNode(node)) {
      node.setFormat(0);
      node.setStyle('');
    }
  });
  $toggleLink(null);
  selection.setFormat(0);
  selection.setStyle('');
}
